import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Pencil } from 'lucide-react';
import toast from '../../../utils/toast';
import {
  plantationService,
  PlantationBudget,
  BudgetVsRealise,
  PlantationCampagne,
} from '../../../services/plantation.service';

interface SuiviBudgetaireTabProps {
  entiteId: string;
  campagnes: PlantationCampagne[];
  campagneActive?: string;
}

const ACTIVITE_LABELS: Record<string, string> = {
  entretien_sol: 'Entretien sol',
  elagage: 'Elagage',
  fertilisation: 'Fertilisation',
  recolte: 'Recolte',
  collecte: 'Collecte regimes',
  transport: 'Transport',
  supervision: 'Supervision',
  sous_traitance: 'Sous-traitance',
  salaires: 'Salaires ouvriers',
};

const ACTIVITE_LABEL_COURT: Record<string, string> = {
  entretien_sol: 'Entretien',
  elagage: 'Elagage',
  fertilisation: 'Fertil.',
  recolte: 'Recolte',
  collecte: 'Collecte',
  transport: 'Transport',
  supervision: 'Superv.',
  sous_traitance: 'Sous-tr.',
  salaires: 'Salaires',
};

const MOIS_LABELS = [
  'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre',
];

const ANNEES = [2023, 2024, 2025, 2026];

const fmt = (n: number) =>
  Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' FCFA';

const couleurTaux = (taux: number) => {
  if (taux >= 100) return 'text-red-600';
  if (taux >= 80) return 'text-amber-600';
  return 'text-green-600';
};

const couleurBarre = (taux: number) => {
  if (taux >= 100) return 'bg-red-500';
  if (taux >= 80) return 'bg-amber-500';
  return 'bg-green-500';
};

const EMPTY_FORM: Partial<PlantationBudget> & { observations: string } = {
  typeActivite: '',
  annee: new Date().getFullYear(),
  mois: 0,
  budgetAlloue: 0,
  realise: 0,
  quantitePlanifiee: undefined,
  unite: '',
  observations: '',
};

export default function SuiviBudgetaireTab({
  entiteId,
  campagnes,
  campagneActive,
}: SuiviBudgetaireTabProps) {
  const [budgets, setBudgets] = useState<BudgetVsRealise[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [filterAnnee, setFilterAnnee] = useState(new Date().getFullYear());
  const [filterMois, setFilterMois] = useState(0); // 0 = toute l'annee
  const [filterCampagne, setFilterCampagne] = useState(campagneActive || '');

  const [showModal, setShowModal] = useState(false);
  const [editActivite, setEditActivite] = useState<string>('');
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const charger = async () => {
    setLoading(true);
    try {
      const data = await plantationService.getBudgetVsRealise(entiteId, {
        campagneId: filterCampagne || undefined,
        annee: filterAnnee,
      });
      setBudgets(data);
    } catch {
      toast.error('Erreur chargement budget');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAnnee, filterMois, filterCampagne]);

  const totaux = useMemo(() => ({
    budgetTotal: budgets.reduce((s, b) => s + b.budgetTotal, 0),
    realiseTotal: budgets.reduce((s, b) => s + b.realiseTotal, 0),
    vra: budgets.reduce((s, b) => s + b.vra, 0),
  }), [budgets]);

  const tauxGlobal = totaux.budgetTotal > 0
    ? Math.round((totaux.realiseTotal / totaux.budgetTotal) * 100)
    : 0;

  const chartData = useMemo(() =>
    budgets.map((b) => ({
      activite: ACTIVITE_LABEL_COURT[b.typeActivite] || b.typeActivite,
      Budget: b.budgetTotal,
      Realise: b.realiseTotal,
    })),
    [budgets],
  );

  const openEdit = (b: BudgetVsRealise) => {
    setEditActivite(b.typeActivite);
    setForm({
      typeActivite: b.typeActivite,
      annee: filterAnnee,
      mois: filterMois || 0,
      budgetAlloue: b.budgetTotal,
      realise: b.realiseTotal,
      quantitePlanifiee: undefined,
      unite: '',
      observations: '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.typeActivite) {
      toast.error('Type d\'activite requis');
      return;
    }
    setSaving(true);
    try {
      await plantationService.upsertBudget(entiteId, {
        typeActivite: form.typeActivite,
        annee: form.annee || filterAnnee,
        mois: form.mois || 0,
        campagneId: filterCampagne || undefined,
        budgetAlloue: form.budgetAlloue || 0,
        realise: form.realise || 0,
        vra: form.realise || 0,
        quantitePlanifiee: form.quantitePlanifiee,
        unite: form.unite || undefined,
      });
      toast.success('Budget mis a jour');
      setShowModal(false);
      charger();
    } catch {
      toast.error('Erreur mise a jour budget');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Annee</label>
          <select
            value={filterAnnee}
            onChange={(e) => setFilterAnnee(Number(e.target.value))}
            className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ANNEES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Mois</label>
          <select
            value={filterMois}
            onChange={(e) => setFilterMois(Number(e.target.value))}
            className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Toute l'annee</option>
            {MOIS_LABELS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Campagne</label>
          <select
            value={filterCampagne}
            onChange={(e) => setFilterCampagne(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Toutes</option>
            {campagnes.map((c) => (
              <option key={c.id} value={c.id}>{c.libelle}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Chargement...</div>
        ) : budgets.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">Aucune donnee budgetaire</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Activite', 'Budget alloue (FCFA)', 'Realise (FCFA)', 'VRA (FCFA)', 'Taux %', 'Progression', 'Action'].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-medium text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {budgets.map((b) => {
                  const taux = b.tauxRealisation ?? (b.budgetTotal > 0 ? Math.round((b.realiseTotal / b.budgetTotal) * 100) : 0);
                  const barWidth = Math.min(taux, 100);
                  return (
                    <tr key={b.typeActivite} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">
                        {ACTIVITE_LABELS[b.typeActivite] || b.typeActivite}
                      </td>
                      <td className="px-3 py-2">{fmt(b.budgetTotal)}</td>
                      <td className="px-3 py-2">{fmt(b.realiseTotal)}</td>
                      <td className="px-3 py-2">{fmt(b.vra)}</td>
                      <td className={`px-3 py-2 font-bold ${couleurTaux(taux)}`}>{taux} %</td>
                      <td className="px-3 py-2 min-w-[100px]">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${couleurBarre(taux)}`}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => openEdit(b)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                          <Pencil size={12} /> Modifier
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t font-bold">
                <tr>
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2">{fmt(totaux.budgetTotal)}</td>
                  <td className="px-3 py-2">{fmt(totaux.realiseTotal)}</td>
                  <td className="px-3 py-2">{fmt(totaux.vra)}</td>
                  <td className={`px-3 py-2 ${couleurTaux(tauxGlobal)}`}>{tauxGlobal} %</td>
                  <td className="px-3 py-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${couleurBarre(tauxGlobal)}`}
                        style={{ width: `${Math.min(tauxGlobal, 100)}%` }}
                      />
                    </div>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Graphique */}
      {chartData.length > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700 mb-3">Budget vs Realise par activite</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} margin={{ top: 0, right: 10, left: 0, bottom: 20 }}>
              <XAxis
                dataKey="activite"
                tick={{ fontSize: 10 }}
                angle={-20}
                textAnchor="end"
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={(v) => Math.round(v / 1000).toString() + ' k'}
              />
              <Tooltip formatter={(v: number) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Budget" fill="#3b82f6" />
              <Bar dataKey="Realise" fill="#22c55e" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Modal modifier budget */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b flex-shrink-0">
              <h2 className="text-base font-semibold text-gray-800">
                Modifier budget — {ACTIVITE_LABELS[editActivite] || editActivite}
              </h2>
            </div>
            <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
              {/* Type activite en lecture seule */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type d'activite</label>
                <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700">
                  {ACTIVITE_LABELS[editActivite] || editActivite}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Annee</label>
                  <select
                    value={form.annee}
                    onChange={(e) => setForm({ ...form, annee: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {ANNEES.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Mois</label>
                  <select
                    value={form.mois || 0}
                    onChange={(e) => setForm({ ...form, mois: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Annuel</option>
                    {MOIS_LABELS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Budget alloue (FCFA)</label>
                  <input
                    type="number"
                    min={0}
                    value={form.budgetAlloue || 0}
                    onChange={(e) => setForm({ ...form, budgetAlloue: parseFloat(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Realise (FCFA) <span className="text-gray-400">(optionnel)</span></label>
                  <input
                    type="number"
                    min={0}
                    value={form.realise || 0}
                    onChange={(e) => setForm({ ...form, realise: parseFloat(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Quantite planifiee</label>
                  <input
                    type="number"
                    min={0}
                    value={form.quantitePlanifiee ?? ''}
                    onChange={(e) => setForm({ ...form, quantitePlanifiee: parseFloat(e.target.value) || undefined })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex : 500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Unite</label>
                  <input
                    type="text"
                    value={form.unite || ''}
                    onChange={(e) => setForm({ ...form, unite: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex : ha, kg, jours"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Observations</label>
                <textarea
                  value={form.observations}
                  onChange={(e) => setForm({ ...form, observations: e.target.value })}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Remarques eventuelles..."
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
